"use client";

import React from "react";
import { $n, $d } from "@/lib/format";
import { makeSpreadOverlay, type SpreadChartOverlay } from "@/components/price-chart";
import { ProfitByDateTable, WeekdayProfitHistogram } from "./profit-breakdown";
import {
  symbolColumn, descriptionColumn, qtyColumn, kindTone, fmtDateRangeCloseTime,
  ColumnTable, type TableColumn,
} from "@/app/trades/summary-columns";

/** One closed credit spread, as every strategy's `/analysis` endpoint reports it. */
export type AnalysisTrade = {
  id: number; underlying: string; strategyType: string;
  openedDate: string; closedDate: string; closedAt: string; expDate: string;
  dte: number; shortStrike: number; longStrike: number; qty: number;
  entrySpot: number; expSpot: number;
  entryCredit: number; entryCreditPerShare: number; exitCost: number;
  maxLoss: number;
  probItm: number | null; epItm: number | null;
  exitProbItm: number | null; exitEpItm: number | null; finishedItm: boolean;
  earlyPnl: number; holdPnl: number;
  daysHeld: number; daysFull: number;
  rorEarly: number; rorHold: number;
  /** Ledger row code (CPSC/CCSC; ZPSC/ZCSC for 0DTE). Derived from strategyType when absent. */
  code?: string;
  /** Which strategy reported the trade — keeps row keys unique when several are merged. */
  source?: string;
  /** What the row is. Absent means a credit spread (the strategy pages). */
  kind?: "spread" | "option" | "equity";
  /** OCC symbol (single option) or ticker (stock); spreads leave it unset. */
  symbol?: string;
  /** Dollars per price point: 100 for options (default), 1 for stock. */
  multiplier?: number;
};

export const tradeKind = (t: AnalysisTrade) => t.kind ?? "spread";

/** The part of an `/analysis` response the pane and the header table draw from. */
export type AnalysisSummary = {
  nTrades: number; nWins: number;
  totalPnl: number; totalCredit: number; totalCapitalAtRisk: number;
  avgDaysEarly: number; avgDaysFull: number;
  skipped: string[];
  trades: AnalysisTrade[];
};

/** All analysis rows are closed credit spreads. CCSC = closed credit call
 *  spread, CPSC = closed credit put spread. */
export function analysisTradeCode(t: AnalysisTrade): string {
  return t.code ?? (t.strategyType.includes("put") ? "CPSC" : "CCSC");
}

/** Shared empty-state note — same look as the strategy pages' panes. */
function emptyNote(text: string) {
  return (
    <div className="inline-block rounded-lg border border-border bg-surface px-3 py-2 mb-[10px]">
      <p className="text-xs text-muted">{text}</p>
    </div>
  );
}

const $$ = (x: number) => x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (x: number, dp = 1) => `${(x * 100).toFixed(dp)}%`;

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className="rounded border border-border bg-surface px-3 py-2 min-w-[140px]">
      <div className="text-[10px] text-muted uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-mono ${tone ?? "text-foreground"}`}>{value}</div>
    </div>
  );
}

/**
 * The Analysis pane body: overall-performance stats, the per-trade ledger, and
 * the profit-by-date tables with their weekday histograms. The strategy pages
 * and the Accounts page all render this one component; the header summary
 * (`AnalysisHeaderTable`) is its companion.
 */
export function AnalysisPane({
  analysis,
  loading,
  error,
  analysisStart,
  origSize,
  openChart,
  emptyText = "No closed credit spreads yet to analyze.",
  stocksOnly = false,
  extraStats,
}: {
  analysis: AnalysisSummary | null;
  loading: boolean;
  error: string | null;
  /** "Returns since" (YYYY-MM-DD) — the window start when set. */
  analysisStart: string;
  /** Account value at the window start, when known. */
  origSize: number | null;
  openChart: (sym: string, overlay: SpreadChartOverlay | null) => void;
  emptyText?: string;
  /** Stock rows only (Stock Trading): no credit or P(ITM) columns. */
  stocksOnly?: boolean;
  /** More tiles after the overall-performance ones. */
  extraStats?: { label: string; value: React.ReactNode; tone?: string }[];
}) {
  return (
    <div className="pt-3">
      {loading && !analysis && (
        <div className="text-muted text-sm">Loading analysis…</div>
      )}
      {error && (
        <div className="text-red text-sm mb-3">Failed to load analysis: {error}</div>
      )}
      {analysis && analysis.nTrades === 0 && emptyNote(emptyText)}
      {analysis && analysis.nTrades > 0 && (() => {
        const a = analysis;
        // Window for annualizing the realized return. It starts at the
        // analysis "From" date when one is set (so the period reflects the
        // chosen window, including any idle time before the first trade);
        // otherwise it falls back to the earliest entry. It ends at the
        // latest exit.
        const earliestOpen = a.trades.reduce((min, t) => t.openedDate < min ? t.openedDate : min, a.trades[0]?.openedDate ?? "");
        const windowStart = analysisStart || earliestOpen;
        const latestClose = a.trades.reduce((max, t) => t.closedDate > max ? t.closedDate : max, a.trades[0]?.closedDate ?? "");
        const periodDays = (() => {
          if (!windowStart || !latestClose) return 0;
          const ms = new Date(latestClose + "T00:00:00").getTime() - new Date(windowStart + "T00:00:00").getTime();
          return Math.max(1, Math.round(ms / 86400000));
        })();
        // Return on peak concurrent capital at risk: per spread
        // (width−credit)×100×qty + open fees; high-water mark over time.
        // Then annualized over the analysis window.
        const absRor = a.totalCapitalAtRisk > 0 ? a.totalPnl / a.totalCapitalAtRisk : 0;
        const annRor = periodDays > 0 ? absRor * (365 / periodDays) : 0;
        return (
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-amber text-xs font-semibold mb-1.5">Overall performance</div>
              <div className="flex flex-wrap gap-2">
                {!stocksOnly && <Stat label="Credit collected" value={`$${$$(a.totalCredit)}`} />}
                <Stat label="Max capital at risk" value={`$${$$(a.totalCapitalAtRisk)}`} />
                <Stat label="Avg days held" value={`${a.avgDaysEarly.toFixed(1)} of ${a.avgDaysFull.toFixed(1)}`} />
                <Stat label="Period" value={`${periodDays}d`} />
                <Stat label="Original account size" value={origSize != null ? $d(origSize) : "—"} />
                {extraStats?.map((x) => <Stat key={x.label} label={x.label} value={x.value} tone={x.tone} />)}
              </div>
            </div>

            {/* Sections 4 & 5 side by side; each scrolls inside a ~15-row box. */}
            <div className="flex gap-6 items-start">
            {/* Section 4: Per-trade breakdown — Ledger */}
            <div className="shrink-0">
              <div className="text-amber text-xs font-semibold mb-1.5">Per-trade detail</div>
              <div className="overflow-y-auto max-h-[360px]">
                {(() => {
                  type Row = AnalysisTrade & { code: string };
                  const rows: Row[] = a.trades.map((t) => ({ ...t, code: analysisTradeCode(t) }));
                  const oc = (
                    id: string,
                    header: React.ReactNode,
                    cell: (r: Row) => React.ReactNode,
                    sortValue?: (r: Row) => number | string | null | undefined,
                  ): TableColumn<Row> => ({
                    id,
                    header: () => header,
                    cell,
                    ...(sortValue ? { sortKey: id, sortValue } : {}),
                  });
                  // Centered header for one-off columns (Days/DTE, Price, Profit/(Loss), RoR).
                  const thC = (t: React.ReactNode) => (
                    <th className="py-1 pr-4 text-center underline align-bottom leading-tight">{t}</th>
                  );
                  const allCols: TableColumn<Row>[] = [
                    oc("dates", <th className="py-1 pr-4 text-left underline">Date/Time</th>,
                      (r) => (
                        <td className="py-1 pr-4 text-left text-muted">
                          {fmtDateRangeCloseTime(r.openedDate, r.closedDate || null, r.closedAt)}
                        </td>
                      ),
                      (r) => r.closedAt || r.closedDate || r.openedDate),
                    oc("symbol", symbolColumn.th(),
                      (r) => symbolColumn.cell(r.code, r.underlying, (sym) => openChart(sym, tradeKind(r) === "spread" ? makeSpreadOverlay({
                        isPut: r.strategyType.includes("put"),
                        shortStrike: r.shortStrike,
                        longStrike: r.longStrike,
                        entryTime: r.openedDate,
                        exitTime: r.closedDate,
                      }) : null)),
                      (r) => r.underlying),
                    oc("desc", descriptionColumn.th(),
                      (r) => descriptionColumn.cell(r.code,
                        tradeKind(r) === "equity"
                          ? { kind: "text", text: r.strategyType === "short_stock" ? "Short stock" : "Stock" }
                          : tradeKind(r) === "option"
                            ? { kind: "option", symbol: r.symbol ?? "" }
                            : {
                              kind: "spread",
                              spreadType: r.strategyType.includes("put") ? "bull_put" : "bear_call",
                              expirationDate: r.expDate,
                              shortStrike: r.shortStrike,
                              longStrike: r.longStrike,
                            }),
                      (r) => r.expDate || r.openedDate),
                    oc("qty", qtyColumn.th(),
                      (r) => qtyColumn.cell(r.code, { kind: "plain", count: r.qty }, { pad: "py-1 pr-4" }),
                      (r) => r.qty),
                    oc("dteDays", thC(<>Days/<br />DTE</>),
                      (r) => <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, tradeKind(r) === "equity" ? `${r.daysHeld} / —` : `${r.daysHeld} / ${r.dte}`)}</td>,
                      (r) => r.daysHeld),
                    oc("probItm", thC(<>P(ITM)/<br />eP(ITM)</>),
                      (r) => {
                        const p = r.probItm == null ? "—" : `${r.probItm.toFixed(0)}%`;
                        const e = r.epItm == null ? "—" : `${(r.epItm * 100).toFixed(0)}%`;
                        return (
                          <td className="py-1 pr-4 text-center font-mono">
                            {kindTone(r.code, `${p} / ${e}`)}
                          </td>
                        );
                      },
                      (r) => r.probItm ?? null),
                    oc("price", thC("Price"),
                      (r) => {
                        // Spreads: credit received vs cost to close (lower is better).
                        // Long stock / long options: bought vs sold (higher is better).
                        // Short single options: credit vs buyback (lower is better).
                        const entry = r.entryCreditPerShare;
                        const mult = r.multiplier ?? 100;
                        const exitPx = r.qty > 0 ? r.exitCost / (mult * r.qty) : 0;
                        const isLong = tradeKind(r) !== "spread" && r.strategyType.startsWith("long");
                        const better = isLong ? exitPx > entry : exitPx < entry;
                        const worse = isLong ? exitPx < entry : exitPx > entry;
                        const exitCls = better ? "text-green" : worse ? "text-red" : "text-foreground";
                        return (
                          <td className="py-1 pr-4 text-center font-mono whitespace-nowrap">
                            {$n(entry)} / <span className={exitCls}>{$n(exitPx)}</span>
                          </td>
                        );
                      },
                      (r) => r.entryCreditPerShare),
                    // Realized P&L for the trade — green when it made money,
                    // red when it lost (an assigned spread settles for a loss).
                    // Risk lives in the RoR column, which is P&L / capital at risk.
                    oc("profitLoss", thC(<>Profit<br />(Loss)</>),
                      (r) => {
                        const fmt$ = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
                        const pnlStr = (r.earlyPnl < 0 ? "-$" : "$") + fmt$(Math.abs(Math.round(r.earlyPnl)));
                        return (
                          <td className="py-1 pr-4 text-center font-mono whitespace-nowrap">
                            <span className={r.earlyPnl >= 0 ? "text-green" : "text-red"}>{pnlStr}</span>
                          </td>
                        );
                      },
                      (r) => r.earlyPnl),
                    oc("ror", thC("RoR"),
                      (r) => <td className="py-1 pr-4 text-center font-mono">{kindTone(r.code, pct(r.rorEarly, 0))}</td>,
                      (r) => r.rorEarly),
                  ];
                  const cols = stocksOnly ? allCols.filter((c) => c.id !== "probItm") : allCols;
                  // Zebra by exit date: all trades closed on the same day
                  // share a stripe; alternate dates newest→oldest.
                  // Class on <tr> paints via tr.exit-stripe-* > td (globals.css).
                  const exitStripeClass = (() => {
                    const uniq = [...new Set(rows.map((r) => r.closedDate).filter(Boolean))]
                      .sort((a, b) => b.localeCompare(a));
                    const map = new Map(uniq.map((d, i) => [d, i % 2 === 0 ? "exit-stripe-a" : "exit-stripe-b"]));
                    return (d: string) => map.get(d) ?? "exit-stripe-b";
                  })();
                  // Bottom line: profit + annualized RoR over the actual
                  // window from earliest entry to latest exit (shared with
                  // the Rate of return section above).
                  const totalProfit = rows.reduce((s, r) => s + r.earlyPnl, 0);
                  const totalQty = rows.reduce((s, r) => s + r.qty, 0);
                  const periodRor = annRor;
                  const fmt$ = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });
                  const profitStr = (totalProfit < 0 ? "-$" : "$") + fmt$(Math.abs(Math.round(totalProfit)));
                  const fmtDate = (iso: string) => {
                    if (!iso) return "—";
                    const d = new Date(iso + "T00:00:00");
                    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
                  };
                  const footer = (
                    <tr className="border-t-2 border-border text-amber font-semibold">
                      <td className="py-1 pr-4 text-left whitespace-nowrap">{fmtDate(earliestOpen)} – {fmtDate(latestClose)}</td>
                      <td className="py-1 pr-4 text-left">TOTAL</td>
                      <td className="py-1 pr-4"></td>
                      <td className="py-1 pr-4 text-center font-mono">{totalQty}</td>
                      <td className="py-1 pr-4 text-center font-mono">{periodDays}d</td>
                      {!stocksOnly && <td className="py-1 pr-4"></td>}
                      <td className="py-1 pr-4"></td>
                      <td className="py-1 pr-4 text-center font-mono whitespace-nowrap">
                        <span className={totalProfit >= 0 ? "text-green" : "text-red"}>{profitStr}</span>
                      </td>
                      <td className="py-1 pr-4 text-center font-mono">{pct(periodRor, 0)}</td>
                    </tr>
                  );
                  return (
                    <ColumnTable<Row>
                      columns={cols}
                      rows={rows}
                      expanded
                      defaultSortKey="dates"
                      defaultSortAsc={false}
                      tableClassName="text-xs whitespace-nowrap [&_th]:align-bottom"
                      headRowClassName="text-amber border-b border-border"
                      renderRow={(r, cells) => (
                        <tr
                          key={`${r.source ?? ""}:${r.id}`}
                          className={`border-b border-border/40 ${exitStripeClass(r.closedDate)}`}
                        >{cells}</tr>
                      )}
                      footer={footer}
                    />
                  );
                })()}
              </div>
            </div>

            <div className="self-stretch border-l-[3px] border-white/25" />

            {/* Section 5: Profit breakdowns — each weekday histogram sits under
                its matching date table and fills that table's width. */}
            <div className="shrink-0 flex flex-wrap gap-10">
              <div className="flex flex-col gap-6 w-fit">
                <ProfitByDateTable trades={a.trades} dateKey="openedDate" label="Profit by Entry Date" verb="Opened" />
                <WeekdayProfitHistogram trades={a.trades} dateKey="openedDate" label="Profit by entry weekday (Mon–Fri)" />
              </div>
              <div className="flex flex-col gap-6 w-fit">
                <ProfitByDateTable trades={a.trades} dateKey="closedDate" label="Profit by Exit Date" verb="Closed" />
                <WeekdayProfitHistogram trades={a.trades} dateKey="closedDate" label="Profit by exit weekday (Mon–Fri)" />
              </div>
            </div>
            </div>

            {a.skipped.length > 0 && (
              <div className="text-[11px] text-muted">
                Skipped: {a.skipped.join("; ")}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
